import React, { useState, useEffect } from "react";
import { C, ROLE_PERMISSIONS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import {
  Landmark, MapPin, Users, Wrench, CheckCircle, Clock,
  AlertCircle, Plus, Search, Eye, Edit, Calendar,
  Star, Target, Hash, Phone, Mail, FileText,
  TrendingUp, Thermometer, Volume2, Droplets, Zap,
  Shield, Home, Layers, ArrowUpRight, XCircle, Mic
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Mosques Page — المساجد
   ═════════════════════════════════════════════ */
export default function MosquesPage({ user }) {
  const [tab, setTab] = useState("mosques");
  const [triggers, setTriggers] = useState({ mosque: 0 });
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "mosques", label: "المساجد", icon: Landmark },
    { key: "maintenance", label: "الصيانة", icon: Wrench },
    { key: "imams", label: "الأئمة والمؤذنون", icon: Mic },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  return (
    <div>
      <ModuleHeader icon={Landmark} title="المساجد" subtitle="إدارة المساجد والصيانة والأئمة والمؤذنين" />

      {perms.canEdit && (
        <div style={mq.row}>
          <button style={mq.btn} onClick={() => fireQuick("mosques", "mosque")}>
            <Landmark size={16} color="#0891b2" />
            <span>رعاية مسجد جديد</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "mosques" && <MosquesTab canEdit={perms.canEdit} triggerOpen={triggers.mosque} />}
      {tab === "maintenance" && <MaintenanceTab canEdit={perms.canEdit} />}
      {tab === "imams" && <ImamsTab canEdit={perms.canEdit} />}
      {tab === "meetings" && <MeetingsTab departmentName="المساجد" />}
      {tab === "transactions" && <TransactionsTab budget={620000} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="mosques" />}
      {tab === "reports" && <ReportsTab departmentName="المساجد" />}
    </div>
  );
}

const mq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ─────────────────────────────────────────────
   ✦ Mosques Tab — قائمة المساجد
   ───────────────────────────────────────────── */
const MOSQUE_FEATURES = ["تكييف مركزي", "مواقف سيارات", "مصلى نساء", "مكتبة", "دورات مياه", "سكن إمام", "حديقة", "مصعد", "إنارة خارجية", "نظام صوت", "كاميرات مراقبة", "خزان ماء"];
const MOSQUE_LESSON_LANGS = ["العربية", "الأردية", "الهندية", "البنغالية", "الفلبينية", "الإنجليزية"];
const MOSQUE_MAINT_TYPES = ["دورية", "طارئة", "تجديد كلي", "تكييف", "كهرباء", "سباكة", "صبغ", "أرضيات"];
const INITIAL_MOSQUE = {
  name: "", district: "", address: "",
  lat: "", lng: "",
  capacity: "", womenCapacity: "", floors: "",
  builtYear: "", licenseNumber: "", area: "",
  imam: "", taraweehImam: "", muezzin: "", fridayKhateeb: "",
  conditionLabel: "ممتاز", lastMaintenance: "",
  features: [], notes: "",
  // dawah activities
  weeklyLessons: "", afterPrayerTalks: "", yearlyCourses: "", tahfeezCircles: "",
  dawahPrograms: "", lessonLanguages: [],
  // maintenance
  lastMaintenanceType: "", currentNeeds: "", maintNotes: "",
};

function MosquesTab({ canEdit, triggerOpen = 0 }) {
  const [mosques, setMosques] = useState(DEMO_MOSQUES);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [form, setForm] = useState(INITIAL_MOSQUE);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const condLabelToKey = { "ممتاز": "excellent", "جيد": "good", "يحتاج صيانة": "needs_maintenance", "تحت الصيانة": "under_maintenance" };

  function toggleMosqueFeature(f) {
    setForm(prev => ({
      ...prev,
      features: prev.features.includes(f) ? prev.features.filter(x => x !== f) : [...prev.features, f],
    }));
  }

  function toggleMosqueLang(l) {
    setForm(prev => ({
      ...prev,
      lessonLanguages: prev.lessonLanguages.includes(l) ? prev.lessonLanguages.filter(x => x !== l) : [...prev.lessonLanguages, l],
    }));
  }

  function handleSave() {
    if (!form.name || !form.district || !form.capacity) {
      showToast("الاسم والحي والسعة مطلوبة", "error");
      return;
    }
    const newM = {
      id: `m${Date.now()}`,
      name: form.name,
      district: form.district,
      address: form.address || "",
      lat: form.lat, lng: form.lng,
      capacity: Number(form.capacity) || 0,
      womenCapacity: Number(form.womenCapacity) || 0,
      floors: Number(form.floors) || 0,
      builtYear: form.builtYear || "",
      licenseNumber: form.licenseNumber,
      area: Number(form.area) || 0,
      imam: form.imam || "—",
      taraweehImam: form.taraweehImam,
      muezzin: form.muezzin || "—",
      fridayKhateeb: form.fridayKhateeb,
      condition: condLabelToKey[form.conditionLabel] || "good",
      lastMaintenance: form.lastMaintenance || "—",
      features: [...form.features],
      notes: form.notes || null,
      weeklyLessons: Number(form.weeklyLessons) || 0,
      afterPrayerTalks: Number(form.afterPrayerTalks) || 0,
      yearlyCourses: Number(form.yearlyCourses) || 0,
      tahfeezCircles: Number(form.tahfeezCircles) || 0,
      dawahPrograms: form.dawahPrograms || "",
      lessonLanguages: [...form.lessonLanguages],
      lastMaintenanceType: form.lastMaintenanceType || "",
      currentNeeds: form.currentNeeds || "",
      maintNotes: form.maintNotes || "",
    };
    setMosques(prev => [...prev, newM]);
    setForm(INITIAL_MOSQUE);
    setShowAdd(false);
    showToast("تمت رعاية المسجد بنجاح");
  }

  const filtered = mosques.filter(m => {
    const matchSearch = m.name.includes(search) || m.district.includes(search);
    const matchStatus = filterStatus === "all" || m.condition === filterStatus;
    return matchSearch && matchStatus;
  });

  const CONDITIONS = [
    { key: "excellent", label: "ممتاز", color: C.success },
    { key: "good", label: "جيد", color: C.teal },
    { key: "needs_maintenance", label: "يحتاج صيانة", color: C.warning },
    { key: "under_maintenance", label: "تحت الصيانة", color: "#7c3aed" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المساجد" value={mosques.length} icon={Landmark} color="#0891b2" />
        <KPICard title="حالة ممتازة" value={mosques.filter(m => m.condition === "excellent").length} icon={CheckCircle} color={C.success} />
        <KPICard title="تحتاج صيانة" value={mosques.filter(m => m.condition === "needs_maintenance").length} icon={AlertCircle} color={C.warning} />
        <KPICard title="تحت الصيانة" value={mosques.filter(m => m.condition === "under_maintenance").length} icon={Wrench} color="#7c3aed" />
        <KPICard title="سعة المصلين" value={mosques.reduce((s, m) => s + m.capacity, 0).toLocaleString()} icon={Users} color={C.primary} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الحي..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {CONDITIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <div style={styles.viewToggle}>
              <button onClick={() => setViewMode("grid")} style={{ ...styles.viewBtn, background: viewMode === "grid" ? C.teal : "transparent", color: viewMode === "grid" ? C.white : C.textLight }}>▦</button>
              <button onClick={() => setViewMode("table")} style={{ ...styles.viewBtn, background: viewMode === "table" ? C.teal : "transparent", color: viewMode === "table" ? C.white : C.textLight }}>☰</button>
            </div>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مسجد</Btn>}
          </div>
        </div>
      </Card>

      {viewMode === "grid" ? (
        <div style={styles.mosquesGrid}>
          {filtered.map(m => (
            <Card key={m.id} style={styles.mosqueCard} onClick={() => setSelected(m)}>
              <div style={styles.mosqueHeader}>
                <div style={{ ...styles.mosqueIcon, background: `${CONDITIONS.find(c => c.key === m.condition)?.color || C.teal}15` }}>
                  <Landmark size={24} color={CONDITIONS.find(c => c.key === m.condition)?.color || C.teal} />
                </div>
                <StatusBadge status={m.condition} statuses={CONDITIONS} />
              </div>
              <h4 style={styles.mosqueName}>{m.name}</h4>
              <p style={styles.mosqueDistrict}><MapPin size={12} /> {m.district}</p>
              <div style={styles.mosqueStats}>
                <div style={styles.mosqueStat}>
                  <Users size={14} color={C.textLight} />
                  <span>{m.capacity} مصلٍ</span>
                </div>
                <div style={styles.mosqueStat}>
                  <Mic size={14} color={C.textLight} />
                  <span>{m.imam}</span>
                </div>
              </div>
              <div style={styles.mosqueFeatures}>
                {m.features.slice(0, 3).map((f, i) => (
                  <span key={i} style={styles.featureTag}>{f}</span>
                ))}
                {m.features.length > 3 && <span style={{ fontSize: 11, color: C.textLight }}>+{m.features.length - 3}</span>}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <DataTable
            columns={[
              { key: "name", label: "اسم المسجد", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
              { key: "district", label: "الحي", width: "120px" },
              { key: "capacity", label: "السعة", width: "90px", render: v => v.toLocaleString() },
              { key: "imam", label: "الإمام", width: "140px" },
              { key: "condition", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={CONDITIONS} /> },
            ]}
            data={filtered}
            onRowClick={row => setSelected(row)}
          />
        </Card>
      )}

      {/* Detail Modal */}
      {selected && (
        <Modal title={`مسجد — ${selected.name}`} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>الحي:</strong> {selected.district}</div>
            <div style={styles.detailRow}><strong>العنوان:</strong> {selected.address}</div>
            <div style={styles.detailRow}><strong>سعة المصلين:</strong> {selected.capacity.toLocaleString()}</div>
            <div style={styles.detailRow}><strong>الإمام:</strong> {selected.imam}</div>
            <div style={styles.detailRow}><strong>المؤذن:</strong> {selected.muezzin}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.condition} statuses={CONDITIONS} /></div>
            <div style={styles.detailRow}><strong>سنة البناء:</strong> {selected.builtYear}</div>
            <div style={styles.detailRow}><strong>آخر صيانة:</strong> {selected.lastMaintenance}</div>
            <div style={styles.detailRow}>
              <strong>المرافق:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                {selected.features.map((f, i) => <span key={i} style={styles.featureTag}>{f}</span>)}
              </div>
            </div>
            {selected.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="رعاية مسجد جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_MOSQUE); }} width={820}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Basic */}
            <Field label="اسم المسجد" required placeholder="مثال: مسجد الإيمان" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الحي" required placeholder="اسم الحي" value={form.district} onChange={v => setForm({ ...form, district: v })} />
              <Field label="العنوان التفصيلي" placeholder="شارع الملك فهد..." value={form.address} onChange={v => setForm({ ...form, address: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="إحداثيات الموقع — Latitude (اختياري)" placeholder="26.3260" value={form.lat} onChange={v => setForm({ ...form, lat: v })} />
              <Field label="إحداثيات الموقع — Longitude (اختياري)" placeholder="43.9750" value={form.lng} onChange={v => setForm({ ...form, lng: v })} />
            </div>

            {/* Capacity */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="سعة المصلين" type="number" required placeholder="0" value={form.capacity} onChange={v => setForm({ ...form, capacity: v })} />
              <Field label="سعة مصلى النساء" type="number" placeholder="0" value={form.womenCapacity} onChange={v => setForm({ ...form, womenCapacity: v })} />
              <Field label="عدد الطوابق" type="number" placeholder="1" value={form.floors} onChange={v => setForm({ ...form, floors: v })} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="سنة البناء" placeholder="1440هـ / 2018م" value={form.builtYear} onChange={v => setForm({ ...form, builtYear: v })} />
              <Field label="رقم رخصة البناء" placeholder="LIC-xxxx" value={form.licenseNumber} onChange={v => setForm({ ...form, licenseNumber: v })} />
              <Field label="المساحة (م²)" type="number" placeholder="0" value={form.area} onChange={v => setForm({ ...form, area: v })} />
            </div>

            {/* Staff */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 6 }}>الكادر</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="الإمام الراتب" placeholder="اسم الإمام" value={form.imam} onChange={v => setForm({ ...form, imam: v })} />
                <Field label="إمام التراويح" placeholder="اسم إمام التراويح" value={form.taraweehImam} onChange={v => setForm({ ...form, taraweehImam: v })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="المؤذن" placeholder="اسم المؤذن" value={form.muezzin} onChange={v => setForm({ ...form, muezzin: v })} />
                <Field label="خطيب الجمعة" placeholder="اسم الخطيب" value={form.fridayKhateeb} onChange={v => setForm({ ...form, fridayKhateeb: v })} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="حالة المسجد" type="select" options={["ممتاز","جيد","يحتاج صيانة","تحت الصيانة"]} value={form.conditionLabel} onChange={v => setForm({ ...form, conditionLabel: v })} />
              <Field label="تاريخ آخر صيانة" type="date" value={form.lastMaintenance} onChange={v => setForm({ ...form, lastMaintenance: v })} />
            </div>

            {/* Dawah Activities */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#7c3aed", borderBottom: `2px solid #7c3aed30`, paddingBottom: 6 }}>الأنشطة الدعوية</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                <Field label="عدد الدروس الأسبوعية" type="number" placeholder="0" value={form.weeklyLessons} onChange={v => setForm({ ...form, weeklyLessons: v })} />
                <Field label="عدد الكلمات بعد الصلوات" type="number" placeholder="0" value={form.afterPrayerTalks} onChange={v => setForm({ ...form, afterPrayerTalks: v })} />
                <Field label="عدد الدورات السنوية" type="number" placeholder="0" value={form.yearlyCourses} onChange={v => setForm({ ...form, yearlyCourses: v })} />
                <Field label="عدد حلقات التحفيظ" type="number" placeholder="0" value={form.tahfeezCircles} onChange={v => setForm({ ...form, tahfeezCircles: v })} />
              </div>
              <Field label="البرامج الدعوية الحالية" type="textarea" placeholder="مثال: درس الفجر يومياً، دورة الفقه الميسر..." rows={2} value={form.dawahPrograms} onChange={v => setForm({ ...form, dawahPrograms: v })} />
              <label style={{ display: "block", marginTop: 10, marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                لغات الدروس ({form.lessonLanguages.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {MOSQUE_LESSON_LANGS.map(l => (
                  <label key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.lessonLanguages.includes(l)} onChange={() => toggleMosqueLang(l)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Facilities */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#16a34a", borderBottom: `2px solid #16a34a30`, paddingBottom: 6 }}>المرافق</h4>
              <label style={{ display: "block", marginBottom: 6, fontSize: 12, color: C.textLight }}>
                ({form.features.length} مختار من {MOSQUE_FEATURES.length})
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {MOSQUE_FEATURES.map(f => (
                  <label key={f} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.features.includes(f)} onChange={() => toggleMosqueFeature(f)} />
                    <span>{f}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Maintenance */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#ea580c", borderBottom: `2px solid #ea580c30`, paddingBottom: 6 }}>الصيانة</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="تاريخ آخر صيانة" type="date" value={form.lastMaintenance} onChange={v => setForm({ ...form, lastMaintenance: v })} />
                <Field label="نوع آخر صيانة" type="select" options={MOSQUE_MAINT_TYPES} value={form.lastMaintenanceType} onChange={v => setForm({ ...form, lastMaintenanceType: v })} />
              </div>
              <Field label="الاحتياجات الحالية" type="textarea" placeholder="مثال: استبدال السجاد، إصلاح التكييف..." rows={2} value={form.currentNeeds} onChange={v => setForm({ ...form, currentNeeds: v })} />
              <Field label="ملاحظات الصيانة" type="textarea" placeholder="ملاحظات حول حالة المسجد..." rows={2} value={form.maintNotes} onChange={v => setForm({ ...form, maintNotes: v })} />
            </div>

            <Field label="ملاحظات عامة" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Landmark} onClick={handleSave}>حفظ المسجد</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_MOSQUE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Maintenance Tab — الصيانة
   ───────────────────────────────────────────── */
const INITIAL_MAINT = { mosque: "", type: "", priority: "عادي", cost: "", description: "", contractor: "" };

function MaintenanceTab({ canEdit }) {
  const [requests, setRequests] = useState(DEMO_MAINTENANCE);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_MAINT);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.mosque || !form.type || !form.description) {
      showToast("المسجد والنوع والوصف مطلوبة", "error");
      return;
    }
    const num = String(requests.length + 1).padStart(3, "0");
    const newReq = {
      id: `mt${Date.now()}`,
      number: `ص-${num}`,
      mosque: form.mosque,
      type: form.type,
      priority: form.priority,
      cost: Number(form.cost) || 0,
      date: new Date().toISOString().slice(0, 10),
      status: "new",
      description: form.description,
      contractor: form.contractor || null,
    };
    setRequests(prev => [newReq, ...prev]);
    setForm(INITIAL_MAINT);
    setShowAdd(false);
    showToast("تم إرسال طلب الصيانة");
  }

  const MAINT_STATUSES = [
    { key: "new", label: "جديد", color: "#2563eb" },
    { key: "approved", label: "معتمد", color: C.teal },
    { key: "in_progress", label: "قيد التنفيذ", color: C.warning },
    { key: "completed", label: "مكتمل", color: C.success },
    { key: "rejected", label: "مرفوض", color: C.danger },
  ];

  const filtered = requests.filter(r => {
    const matchSearch = r.mosque.includes(search) || r.type.includes(search);
    const matchStatus = filterStatus === "all" || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="طلبات الصيانة" value={requests.length} icon={Wrench} color="#7c3aed" />
        <KPICard title="قيد التنفيذ" value={requests.filter(r => r.status === "in_progress").length} icon={Clock} color={C.warning} />
        <KPICard title="مكتملة" value={requests.filter(r => r.status === "completed").length} icon={CheckCircle} color={C.success} />
        <KPICard title="التكلفة الإجمالية" value={`${(requests.reduce((s, r) => s + r.cost, 0) / 1000).toFixed(0)}K`} icon={TrendingUp} color={C.accent} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالمسجد أو نوع الصيانة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {MAINT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">طلب صيانة</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "رقم الطلب", width: "100px", render: v => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
            { key: "mosque", label: "المسجد" },
            { key: "type", label: "نوع الصيانة", width: "130px" },
            { key: "priority", label: "الأولوية", width: "90px", render: v => (
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: v === "عاجل" ? `${C.danger}15` : v === "مرتفع" ? `${C.warning}15` : `${C.teal}15`,
                color: v === "عاجل" ? C.danger : v === "مرتفع" ? C.warning : C.teal,
              }}>{v}</span>
            )},
            { key: "cost", label: "التكلفة", width: "100px", render: v => `${v.toLocaleString()} ر` },
            { key: "date", label: "التاريخ", width: "100px" },
            { key: "status", label: "الحالة", width: "110px", render: v => <StatusBadge status={v} statuses={MAINT_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`طلب صيانة ${selected.number}`} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>رقم الطلب:</strong> {selected.number}</div>
            <div style={styles.detailRow}><strong>المسجد:</strong> {selected.mosque}</div>
            <div style={styles.detailRow}><strong>نوع الصيانة:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>الأولوية:</strong> {selected.priority}</div>
            <div style={styles.detailRow}><strong>التكلفة:</strong> {selected.cost.toLocaleString()} ريال</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
            <div style={styles.detailRow}><strong>المقاول:</strong> {selected.contractor || "لم يحدد بعد"}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="طلب صيانة جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_MAINT); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="المسجد" type="select" options={DEMO_MOSQUES.map(m => m.name)} required value={form.mosque} onChange={v => setForm({ ...form, mosque: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="نوع الصيانة" type="select" options={["كهرباء","تكييف","سباكة","دهان","نظافة","تجديد سجاد","إنارة خارجية","أخرى"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="الأولوية" type="select" options={["عادي","مرتفع","عاجل"]} required value={form.priority} onChange={v => setForm({ ...form, priority: v })} />
            </div>
            <Field label="التكلفة التقديرية (ريال)" type="number" placeholder="0" value={form.cost} onChange={v => setForm({ ...form, cost: v })} />
            <Field label="وصف المشكلة" type="textarea" required placeholder="صف المشكلة بالتفصيل..." rows={4} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <Field label="المقاول" placeholder="اسم المقاول (اختياري)" value={form.contractor} onChange={v => setForm({ ...form, contractor: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Wrench} onClick={handleSave}>إرسال الطلب</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_MAINT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Imams Tab — الأئمة والمؤذنون
   ───────────────────────────────────────────── */
const IMAM_NATIONALITIES = ["سعودي", "مصري", "سوداني", "يمني", "أردني", "سوري", "هندي", "باكستاني", "أخرى"];
const IMAM_ROLES = ["إمام", "مؤذن", "خطيب"];
const INITIAL_IMAM = {
  name: "", nationality: "", role: "إمام", mosque: "",
  phone: "", qualification: "", startDate: "", salary: "",
  fridayKhutba: false, taraweeh: false, quranTeacher: false,
  notes: "",
};

function ImamsTab({ canEdit }) {
  const [staff, setStaff] = useState(DEMO_IMAMS);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_IMAM);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.name || !form.mosque) {
      showToast("الاسم والمسجد مطلوبان", "error");
      return;
    }
    const newS = {
      id: `i${Date.now()}`,
      name: form.name,
      nationality: form.nationality,
      role: form.role,
      mosque: form.mosque,
      phone: form.phone,
      qualification: form.qualification,
      startDate: form.startDate || "",
      salary: Number(form.salary) || 0,
      fridayKhutba: form.fridayKhutba,
      taraweeh: form.taraweeh,
      quranTeacher: form.quranTeacher,
      notes: form.notes,
    };
    setStaff(prev => [...prev, newS]);
    setForm(INITIAL_IMAM);
    setShowAdd(false);
    showToast("تمت الإضافة");
  }

  const filtered = staff.filter(s => {
    const matchSearch = s.name.includes(search) || s.mosque.includes(search);
    const matchRole = filterRole === "all" || s.role === filterRole;
    return matchSearch && matchRole;
  });

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الكادر" value={staff.length} icon={Users} color="#0891b2" />
        <KPICard title="أئمة" value={staff.filter(s => s.role === "إمام").length} icon={Star} color={C.primary} />
        <KPICard title="مؤذنون" value={staff.filter(s => s.role === "مؤذن").length} icon={Volume2} color={C.teal} />
        <KPICard title="خطباء جمعة" value={staff.filter(s => s.fridayKhutba).length} icon={Mic} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو المسجد..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={styles.filterSelect}>
              <option value="all">الكل</option>
              <option value="إمام">أئمة</option>
              <option value="مؤذن">مؤذنون</option>
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة</Btn>}
          </div>
        </div>
      </Card>

      <div style={styles.imamsGrid}>
        {filtered.map(s => (
          <Card key={s.id} style={styles.imamCard} onClick={() => setSelected(s)}>
            <div style={styles.imamTop}>
              <div style={{
                ...styles.imamAvatar,
                background: s.role === "إمام"
                  ? `linear-gradient(135deg, ${C.primary}, ${C.teal})`
                  : `linear-gradient(135deg, #7c3aed, #a78bfa)`,
              }}>
                {s.name.charAt(0)}
              </div>
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: s.role === "إمام" ? `${C.primary}12` : `#7c3aed12`,
                color: s.role === "إمام" ? C.primary : "#7c3aed",
              }}>{s.role}</span>
            </div>
            <h4 style={{ margin: "10px 0 2px", fontSize: 15, fontWeight: 700, color: C.primary }}>{s.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: C.teal, fontWeight: 500 }}>
              <Landmark size={12} /> {s.mosque}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {s.fridayKhutba && <span style={{ ...styles.featureTag, background: `${C.accent}15`, color: "#8b6914" }}>خطيب جمعة</span>}
              {s.taraweeh && <span style={{ ...styles.featureTag, background: `${C.success}12`, color: C.success }}>تراويح</span>}
              {s.quranTeacher && <span style={{ ...styles.featureTag, background: `${C.teal}12`, color: C.teal }}>معلم قرآن</span>}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: C.textLight, display: "flex", alignItems: "center", gap: 4 }}>
              <Phone size={12} /> <span dir="ltr">{s.phone}</span>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={`${selected.role} — ${selected.name}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>الدور:</strong> {selected.role}</div>
            <div style={styles.detailRow}><strong>المسجد:</strong> {selected.mosque}</div>
            <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
            <div style={styles.detailRow}><strong>خطيب جمعة:</strong> {selected.fridayKhutba ? "نعم" : "لا"}</div>
            <div style={styles.detailRow}><strong>صلاة التراويح:</strong> {selected.taraweeh ? "نعم" : "لا"}</div>
            <div style={styles.detailRow}><strong>معلم قرآن:</strong> {selected.quranTeacher ? "نعم" : "لا"}</div>
            <div style={styles.detailRow}><strong>تاريخ التعيين:</strong> {selected.startDate}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة إمام / مؤذن / خطيب" onClose={() => { setShowAdd(false); setForm(INITIAL_IMAM); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="الاسم الكامل" required placeholder="الاسم الثلاثي" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="الجنسية" type="select" options={IMAM_NATIONALITIES} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="الدور" type="select" options={IMAM_ROLES} required value={form.role} onChange={v => setForm({ ...form, role: v })} />
              <Field label="المسجد" type="select" options={DEMO_MOSQUES.map(m => m.name)} required value={form.mosque} onChange={v => setForm({ ...form, mosque: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="رقم الجوال" placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="المؤهل" placeholder="مثال: بكالوريوس شريعة" value={form.qualification} onChange={v => setForm({ ...form, qualification: v })} />
              <Field label="تاريخ التعيين" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
            </div>
            <Field label="الراتب الشهري (ر.س)" type="number" placeholder="0" value={form.salary} onChange={v => setForm({ ...form, salary: v })} />

            {/* Role flags */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>المهام الإضافية</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: 10, background: C.bg, borderRadius: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.fridayKhutba} onChange={e => setForm({ ...form, fridayKhutba: e.target.checked })} />
                  <span>خطيب جمعة</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.taraweeh} onChange={e => setForm({ ...form, taraweeh: e.target.checked })} />
                  <span>تراويح</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.quranTeacher} onChange={e => setForm({ ...form, quranTeacher: e.target.checked })} />
                  <span>معلم قرآن</span>
                </label>
              </div>
            </div>

            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Mic} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_IMAM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const DEMO_MOSQUES = [
  { id: "m1", name: "مسجد التوحيد", district: "الروضة", address: "حي الروضة — شارع الملك فهد", capacity: 800, imam: "الشيخ سعد القحطاني", muezzin: "أحمد العلي", condition: "excellent", builtYear: "1425هـ", lastMaintenance: "1445/06", features: ["تكييف مركزي","مواقف","مصلى نساء","مكتبة","دورات مياه"], notes: null },
  { id: "m2", name: "مسجد الإيمان", district: "النزهة", address: "حي النزهة — شارع عمر بن الخطاب", capacity: 600, imam: "الشيخ فهد المطيري", muezzin: "خالد السبيعي", condition: "good", builtYear: "1430هـ", lastMaintenance: "1445/09", features: ["تكييف مركزي","مواقف","مصلى نساء"], notes: null },
  { id: "m3", name: "مسجد الفرقان", district: "الصفا", address: "حي الصفا — شارع الأمير نايف", capacity: 450, imam: "الشيخ عبدالله الحربي", muezzin: "ناصر الدوسري", condition: "needs_maintenance", builtYear: "1420هـ", lastMaintenance: "1444/03", features: ["تكييف","مواقف","دورات مياه"], notes: "يحتاج صيانة للتكييف وإعادة دهان" },
  { id: "m4", name: "مسجد السلام", district: "الخليج", address: "حي الخليج — شارع الملك عبدالعزيز", capacity: 350, imam: "الشيخ يوسف الشمري", muezzin: "عمر البقمي", condition: "excellent", builtYear: "1435هـ", lastMaintenance: "1446/01", features: ["تكييف مركزي","مواقف","مصلى نساء","مكتبة"], notes: null },
  { id: "m5", name: "مسجد الهدى", district: "الربيع", address: "حي الربيع — شارع الأمير سلطان", capacity: 500, imam: "الشيخ محمد العنزي", muezzin: "سلطان الزهراني", condition: "good", builtYear: "1428هـ", lastMaintenance: "1445/11", features: ["تكييف","مواقف","دورات مياه","مكتبة"], notes: null },
  { id: "m6", name: "مسجد الرحمة", district: "الوادي", address: "حي الوادي — شارع أبو بكر الصديق", capacity: 300, imam: "الشيخ إبراهيم السالم", muezzin: "طلال الحميدي", condition: "under_maintenance", builtYear: "1418هـ", lastMaintenance: "1446/04", features: ["تكييف","دورات مياه"], notes: "جاري تجديد شامل — سجاد وتكييف ودهان" },
  { id: "m7", name: "مسجد النور", district: "الشفا", address: "حي الشفا — شارع خالد بن الوليد", capacity: 400, imam: "الشيخ حمد الخالدي", muezzin: "فيصل المالكي", condition: "excellent", builtYear: "1432هـ", lastMaintenance: "1445/08", features: ["تكييف مركزي","مواقف","مصلى نساء","مكتبة","حديقة"], notes: null },
  { id: "m8", name: "مسجد البر", district: "السليمانية", address: "حي السليمانية — شارع عثمان بن عفان", capacity: 250, imam: "الشيخ عبدالرحمن السويلم", muezzin: "ماجد القرني", condition: "needs_maintenance", builtYear: "1415هـ", lastMaintenance: "1443/12", features: ["تكييف","دورات مياه"], notes: "يحتاج تجديد السجاد والإنارة" },
];

const DEMO_MAINTENANCE = [
  { id: "mt1", number: "ص-001", mosque: "مسجد الفرقان", type: "تكييف", priority: "عاجل", cost: 45000, date: "1446/04/01", status: "in_progress", description: "صيانة شاملة لنظام التكييف المركزي — تغيير 4 وحدات", contractor: "مؤسسة التبريد الحديثة" },
  { id: "mt2", number: "ص-002", mosque: "مسجد الرحمة", type: "تجديد شامل", priority: "مرتفع", cost: 120000, date: "1446/03/15", status: "in_progress", description: "تجديد شامل يشمل السجاد والدهان والتكييف والإنارة", contractor: "مؤسسة الإعمار" },
  { id: "mt3", number: "ص-003", mosque: "مسجد الفرقان", type: "دهان", priority: "عادي", cost: 25000, date: "1446/04/10", status: "approved", description: "إعادة دهان المسجد من الداخل والخارج", contractor: null },
  { id: "mt4", number: "ص-004", mosque: "مسجد البر", type: "سجاد", priority: "مرتفع", cost: 35000, date: "1446/04/05", status: "new", description: "تغيير سجاد المسجد بالكامل — مساحة 400 متر مربع", contractor: null },
  { id: "mt5", number: "ص-005", mosque: "مسجد البر", type: "إنارة", priority: "عادي", cost: 18000, date: "1446/04/08", status: "new", description: "تغيير إنارة المسجد الداخلية والخارجية بـ LED", contractor: null },
  { id: "mt6", number: "ص-006", mosque: "مسجد التوحيد", type: "سباكة", priority: "عادي", cost: 8000, date: "1446/02/20", status: "completed", description: "إصلاح تسريب في دورات المياه", contractor: "مؤسسة السلامة" },
  { id: "mt7", number: "ص-007", mosque: "مسجد الهدى", type: "كهرباء", priority: "مرتفع", cost: 15000, date: "1446/01/10", status: "completed", description: "تجديد اللوحة الكهربائية الرئيسية", contractor: "مؤسسة النور الكهربائية" },
];

const DEMO_IMAMS = [
  { id: "i1", name: "الشيخ سعد القحطاني", role: "إمام", mosque: "مسجد التوحيد", phone: "0551112233", fridayKhutba: true, taraweeh: true, quranTeacher: true, startDate: "1430هـ" },
  { id: "i2", name: "الشيخ فهد المطيري", role: "إمام", mosque: "مسجد الإيمان", phone: "0552223344", fridayKhutba: true, taraweeh: true, quranTeacher: false, startDate: "1435هـ" },
  { id: "i3", name: "الشيخ عبدالله الحربي", role: "إمام", mosque: "مسجد الفرقان", phone: "0553334455", fridayKhutba: true, taraweeh: false, quranTeacher: true, startDate: "1432هـ" },
  { id: "i4", name: "الشيخ يوسف الشمري", role: "إمام", mosque: "مسجد السلام", phone: "0554445566", fridayKhutba: false, taraweeh: true, quranTeacher: true, startDate: "1438هـ" },
  { id: "i5", name: "الشيخ محمد العنزي", role: "إمام", mosque: "مسجد الهدى", phone: "0555556677", fridayKhutba: true, taraweeh: true, quranTeacher: false, startDate: "1436هـ" },
  { id: "i6", name: "الشيخ إبراهيم السالم", role: "إمام", mosque: "مسجد الرحمة", phone: "0556667788", fridayKhutba: false, taraweeh: false, quranTeacher: true, startDate: "1440هـ" },
  { id: "i7", name: "أحمد العلي", role: "مؤذن", mosque: "مسجد التوحيد", phone: "0557778899", fridayKhutba: false, taraweeh: false, quranTeacher: false, startDate: "1433هـ" },
  { id: "i8", name: "خالد السبيعي", role: "مؤذن", mosque: "مسجد الإيمان", phone: "0558889900", fridayKhutba: false, taraweeh: false, quranTeacher: false, startDate: "1437هـ" },
  { id: "i9", name: "ناصر الدوسري", role: "مؤذن", mosque: "مسجد الفرقان", phone: "0559990011", fridayKhutba: false, taraweeh: false, quranTeacher: false, startDate: "1434هـ" },
  { id: "i10", name: "الشيخ حمد الخالدي", role: "إمام", mosque: "مسجد النور", phone: "0551234500", fridayKhutba: true, taraweeh: true, quranTeacher: true, startDate: "1434هـ" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16,
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer",
  },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  /* View toggle */
  viewToggle: {
    display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`,
  },
  viewBtn: {
    padding: "6px 10px", border: "none", cursor: "pointer", fontSize: 14,
    fontFamily: "'Readex Pro'", transition: "all 0.2s",
  },

  /* Mosques Grid */
  mosquesGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16,
  },
  mosqueCard: { padding: 20, cursor: "pointer" },
  mosqueHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  mosqueIcon: {
    width: 48, height: 48, borderRadius: 12, display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  mosqueName: { margin: "12px 0 2px", fontSize: 16, fontWeight: 700, color: C.primary },
  mosqueDistrict: {
    margin: 0, fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 4,
  },
  mosqueStats: {
    display: "flex", gap: 16, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}`,
  },
  mosqueStat: {
    display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.textLight,
  },
  mosqueFeatures: {
    display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10,
  },
  featureTag: {
    padding: "3px 8px", background: `${C.teal}10`, color: C.teal,
    borderRadius: 6, fontSize: 11, fontWeight: 500,
  },

  /* Imams Grid */
  imamsGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16,
  },
  imamCard: { padding: 18, cursor: "pointer" },
  imamTop: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  imamAvatar: {
    width: 46, height: 46, borderRadius: 12, color: C.white,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 18,
  },
};
